import { isDefined } from "@clipboard-health/util-ts";
// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { LiveActivity } from "@clipboardhealth/capacitor-live-activity";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { APP_V2_APP_EVENTS } from "../lib/analytics/events";
import { logEvent } from "../lib/analytics/log";
import { getDeviceInfo } from "../lib/utils/capacitor/deviceUtils";
import { WorkplaceType } from "../redesign/Workplace/constants";
import { useDefinedWorker } from "../Worker/useDefinedWorker";
import { createLiveActivityContentStatePayload } from "./createLiveActivityContentStatePayload";
import { generateLiveActivityId } from "./generateLiveActivityId";
import { getClipboardScoreProfileForLiveActivity } from "./getClipboardScoreProfileForLiveActivity";
import { isDeviceEligibleForLiveActivities } from "./isDeviceEligibleForLiveActivities";
import { isShiftDoubleBooked } from "./isShiftDoubleBooked";
import { type LiveActivityShift, type LiveActivityWorkplace } from "./types";
import { useLiveActivitiesConfig } from "./useLiveActivitiesConfig";

interface UseStartShiftLiveActivityParams {
  shift: LiveActivityShift;
  workplace: LiveActivityWorkplace;
}

export function useStartLiveActivityMutation() {
  const worker = useDefinedWorker();
  const queryClient = useQueryClient();

  const { isFeatureEnabled } = useLiveActivitiesConfig();

  return useMutation({
    mutationFn: async (params: UseStartShiftLiveActivityParams) => {
      const { shift, workplace } = params;

      const isDeviceEligible = await isDeviceEligibleForLiveActivities();

      if (!isDeviceEligible) {
        logEvent(APP_V2_APP_EVENTS.LIVE_ACTIVITIES_SKIPPING_CREATION, {
          workerId: worker.userId,
          shiftId: shift.id,
          reason: "Device is not eligible for live activities",
        });
        return;
      }

      if (!isFeatureEnabled) {
        logEvent(APP_V2_APP_EVENTS.LIVE_ACTIVITIES_SKIPPING_CREATION, {
          workerId: worker.userId,
          shiftId: shift.id,
          reason: "Feature flag is disabled",
        });
        return;
      }

      if (workplace.type === WorkplaceType.JOB_FAIR) {
        logEvent(APP_V2_APP_EVENTS.LIVE_ACTIVITIES_SKIPPING_CREATION, {
          workerId: worker.userId,
          shiftId: shift.id,
          reason: "Workplace type is Job Fair",
        });
        return;
      }

      const isDoubleBookedShift = await isShiftDoubleBooked({
        shiftStart: shift.start,
        workerId: worker.userId,
        workplaceId: workplace.id,
      });

      if (isDoubleBookedShift) {
        logEvent(APP_V2_APP_EVENTS.LIVE_ACTIVITIES_SKIPPING_CREATION, {
          workerId: worker.userId,
          shiftId: shift.id,
          reason: "Shift is double booked",
        });
        return;
      }

      const deviceInfo = await getDeviceInfo();

      const activityId = generateLiveActivityId({
        workerId: worker.userId,
        shiftId: shift.id,
        deviceId: deviceInfo.deviceId,
      });

      const currentActivities = await LiveActivity.getCurrentState({ activityId });
      if (currentActivities.activities.length > 0) {
        logEvent(APP_V2_APP_EVENTS.LIVE_ACTIVITIES_SKIPPING_CREATION, {
          workerId: worker.userId,
          shiftId: shift.id,
          reason: "Live Activity already exists",
        });
        return;
      }

      const { onTimePointsReward, latePointsDeduction } =
        await getClipboardScoreProfileForLiveActivity({
          workerId: worker.userId,
          qualification: shift.qualification,
          queryClient,
        });

      if (!isDefined(onTimePointsReward) || !isDefined(latePointsDeduction)) {
        logEvent(APP_V2_APP_EVENTS.LIVE_ACTIVITIES_SKIPPING_CREATION, {
          workerId: worker.userId,
          shiftId: shift.id,
          reason: "No clipboard score profile found",
        });
        return;
      }

      const contentStatePayload = createLiveActivityContentStatePayload({
        shift,
        workplace,
        workerId: worker.userId,
        clipboardScore: {
          clockInOnTime: onTimePointsReward,
          latenessPenalty: latePointsDeduction,
        },
      });

      logEvent(APP_V2_APP_EVENTS.LIVE_ACTIVITIES_STARTING_CREATION, {
        activityId,
        workerId: worker.userId,
        shiftId: shift.id,
        contentStatePayload,
      });

      await LiveActivity.createAndStartActivity({
        data: contentStatePayload,
        activityId,
      });
    },
  });
}
